// Owns the document's sign-in session outside React, so the router can ask
// "is this user signed in?" before it renders anything (see
// screens/app/authMiddleware).
//
// Lifecycle, unchanged from when the session lived in AuthSessionProvider:
// one session per visit to the app shell. It starts when the shell is entered
// (at boot, or from the shell route's middleware) and ends when the shell
// unmounts, so returning from the shell-less /login page starts a fresh one.
//
// The UserManager is created once per document, at boot.

import { createAuthSession } from "./authSession";
import { EXTERNAL_STATUS } from "./authStateMachine";
import { getInitializedUserManager } from "./oidcManagerService";

let router = null;
let userManagerPromise = null;
let active = null;

// Before the router exists: creating the router starts its first navigation,
// whose middleware needs the runtime. userManager: tests pass one (or a
// promise of one); the app creates it from the provider settings.
export const initAuthRuntime = ({ userManager } = {}) => {
  router = null;
  active?.release();
  active = null;
  userManagerPromise = userManager
    ? Promise.resolve(userManager)
    : getInitializedUserManager();
  userManagerPromise.catch((error) => {
    console.error("Unable to create the OIDC UserManager.", error);
  });
};

// The data router, for navigation and the pending location's #hash.
export const setAuthRouter = (appRouter) => {
  router = appRouter;
};

export const getUserManagerPromise = () => userManagerPromise;

const navigate = (to, options) => router?.navigate(to, options);

// The shell's session, started if there isn't one yet.
export const acquireAuthSession = () => {
  if (active) {
    return active.session;
  }

  const session = createAuthSession({ navigate });
  const detach = session.attach();
  const entry = { session, stop: undefined, released: false };
  entry.release = () => {
    entry.released = true;
    entry.stop?.();
    detach();
  };
  userManagerPromise?.then(
    (userManager) => {
      if (!entry.released) {
        entry.stop = session.start(userManager);
      }
    },
    () => {},
  );

  active = entry;
  return session;
};

// Called when the shell unmounts. A later acquire starts a fresh session.
export const releaseAuthSession = (session) => {
  if (active?.session === session) {
    active.release();
    active = null;
  }
};

// Resolves with the session's status once it is no longer loading.
export const waitForAuthDecision = (session) =>
  new Promise((resolve) => {
    if (session.getStatus() !== EXTERNAL_STATUS.LOADING) {
      resolve(session.getStatus());
      return;
    }
    const unsubscribe = session.subscribe((status) => {
      if (status !== EXTERNAL_STATUS.LOADING) {
        unsubscribe();
        resolve(status);
      }
    });
  });

// Where the router is going (or, on the first load, is). Middleware sees only
// the request URL, which never carries the #hash. Before the router is attached
// the first load is the document's own URL.
export const getPendingLocation = () =>
  router
    ? (router.state.navigation.location ?? router.state.location)
    : globalThis.location;
